import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, KeyRound, User, Shield, Mail, Image as ImageIcon, Trash2, Plus, Pencil, Save, FileText } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (currentPassword === newPassword) {
      toast.error('New password must be different from current password');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your account settings</p>
      </div>

      {/* Account Info */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <User size={20} className="text-primary" />
          <h2 className="text-lg font-semibold">Account Information</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-muted-foreground">Email</Label>
            <p className="font-medium">{user?.email}</p>
          </div>
          <div>
            <Label className="text-muted-foreground">Role</Label>
            <p className="font-medium capitalize">{user?.role}</p>
          </div>
        </div>
      </Card>

      {/* Change Password */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <KeyRound size={20} className="text-primary" />
          <h2 className="text-lg font-semibold">Change Password</h2>
        </div>
        
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Current Password</Label>
            <div className="relative">
              <Lock
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                size={18}
              />
              <Input
                id="currentPassword"
                type={showCurrentPassword ? 'text' : 'password'}
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="pl-10 pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">New Password</Label>
            <div className="relative">
              <KeyRound
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                size={18}
              />
              <Input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="pl-10 pr-10"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Must be at least 8 characters
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <div className="relative">
              <Lock
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                size={18}
              />
              <Input
                id="confirmPassword"
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10"
                required
              />
            </div>
          </div>

          <Button type="submit" disabled={loading}>
            {loading ? 'Changing Password...' : 'Change Password'}
          </Button>
        </form>
      </Card>

      {/* Company email signature + templates are admin-only (server enforces too) */}
      {user?.role === 'admin' && <EmailSignatureCard />}
      {user?.role === 'admin' && <EmailTemplatesCard />}

      {/* Security Info */}
      <Card className="p-6 bg-muted/50">
        <div className="flex items-start gap-3">
          <Shield className="text-primary mt-0.5" size={20} />
          <div>
            <h3 className="font-medium mb-1">Security Tips</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Use a unique password you don't use elsewhere</li>
              <li>• Include uppercase, lowercase, numbers, and special characters</li>
              <li>• Never share your password with anyone</li>
              <li>• Your session will automatically expire after 15 minutes of inactivity</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}

// =============================================================
// EMAIL SIGNATURE (with logo) — applies to emails you send
// =============================================================
function EmailSignatureCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState({ signature_html: '', signature_enabled: true, logo_url: null, logo_width: 160 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/email-settings`);
      setSettings(data);
    } catch (e) {
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/email-settings`, {
        signature_html: settings.signature_html,
        signature_enabled: settings.signature_enabled,
        logo_width: Number(settings.logo_width) || 160,
      });
      setSettings(data);
      toast.success('Signature saved');
    } catch (e) {
      toast.error('Failed to save signature');
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/email-settings/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSettings((s) => ({ ...s, logo_url: data.logo_url, logo_file_id: data.logo_file_id }));
      toast.success('Logo uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Logo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    try {
      await axios.delete(`${API}/email-settings/logo`);
      setSettings((s) => ({ ...s, logo_url: null, logo_file_id: null }));
      toast.success('Logo removed');
    } catch (e) {
      toast.error('Failed to remove logo');
    }
  };

  return (
    <Card className="p-6" data-testid="email-signature-card">
      <div className="flex items-center gap-3 mb-4">
        <Mail size={20} className="text-primary" />
        <h2 className="text-lg font-semibold">Email Signature</h2>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <Label className="text-sm text-muted-foreground">Enabled</Label>
          <Switch
            checked={!!settings.signature_enabled}
            onCheckedChange={(v) => setSettings((s) => ({ ...s, signature_enabled: v }))}
            data-testid="signature-enabled-switch"
          />
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        This signature and logo are automatically added to the bottom of emails you send from the app
        (Communications and prospect emails).
      </p>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-4 max-w-2xl">
          <div>
            <Label className="text-sm">Logo</Label>
            <div className="flex items-center gap-4 mt-1">
              {settings.logo_url ? (
                <img src={settings.logo_url} alt="logo" className="h-12 border rounded bg-white p-1" data-testid="signature-logo-preview" />
              ) : (
                <div className="h-12 w-24 border border-dashed rounded flex items-center justify-center text-muted-foreground">
                  <ImageIcon size={18} />
                </div>
              )}
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="signature-logo-input" />
                <span className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border hover:bg-muted">
                  {uploading ? 'Uploading…' : (settings.logo_url ? 'Replace logo' : 'Upload logo')}
                </span>
              </label>
              {settings.logo_url && (
                <Button variant="ghost" size="sm" onClick={removeLogo} className="text-red-600" data-testid="signature-logo-remove">
                  <Trash2 size={14} className="mr-1" /> Remove
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Logo width (px)</Label>
              <Input type="number" min={40} max={400} value={settings.logo_width || 160}
                onChange={(e) => setSettings((s) => ({ ...s, logo_width: e.target.value }))}
                data-testid="signature-logo-width" />
            </div>
          </div>

          <div>
            <Label className="text-sm">Signature</Label>
            <Textarea
              rows={5}
              value={settings.signature_html || ''}
              onChange={(e) => setSettings((s) => ({ ...s, signature_html: e.target.value }))}
              placeholder={"Jane Smith\nRecruiting Director, Elite Home Rehab\n(512) 555-0100 · jane@elitehomerehab.com"}
              data-testid="signature-html-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Plain text or basic HTML. Line breaks are preserved.</p>
          </div>

          {(settings.signature_html || settings.logo_url) && (
            <div>
              <Label className="text-sm">Preview</Label>
              <div className="mt-1 border rounded p-3 bg-white text-sm" data-testid="signature-preview">
                {settings.logo_url && <img src={settings.logo_url} alt="logo" style={{ width: `${settings.logo_width || 160}px` }} className="mb-2" />}
                <div style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: settings.signature_html || '' }} />
              </div>
            </div>
          )}

          <Button onClick={save} disabled={saving} data-testid="signature-save-btn">
            <Save size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save signature'}
          </Button>
        </div>
      )}
    </Card>
  );
}

// =============================================================
// EMAIL TEMPLATES — reusable subject/body for prospect emails
// =============================================================
const EMPTY_TPL = { name: '', subject: '', body_html: '' };

function EmailTemplatesCard() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // template object or null
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/email-templates`);
      setTemplates(data || []);
    } catch (e) {
      toast.error('Failed to load templates');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.name.trim() || !editing.subject.trim()) {
      toast.error('Name and subject are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { name: editing.name, subject: editing.subject, body_html: editing.body_html };
      if (editing.id) {
        await axios.put(`${API}/email-templates/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/email-templates`, payload);
      }
      toast.success('Template saved');
      setEditing(null);
      load();
    } catch (e) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/email-templates/${id}`);
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <Card className="p-6" data-testid="email-templates-card">
      <div className="flex items-center gap-3 mb-4">
        <FileText size={20} className="text-primary" />
        <h2 className="text-lg font-semibold">Email Templates</h2>
        <div className="flex-1" />
        {!editing && (
          <Button size="sm" onClick={() => setEditing({ ...EMPTY_TPL })} data-testid="template-add-btn">
            <Plus size={14} className="mr-1" /> New template
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Reusable subject/body you can pick when emailing a prospect. Use <code>{'{{first_name}}'}</code> and <code>{'{{last_name}}'}</code> for personalization.
      </p>

      {editing ? (
        <div className="space-y-3 max-w-2xl border rounded-md p-4 bg-muted/20">
          <div>
            <Label className="text-sm">Template name</Label>
            <Input value={editing.name} onChange={(e) => setEditing((t) => ({ ...t, name: e.target.value }))} data-testid="template-name-input" />
          </div>
          <div>
            <Label className="text-sm">Subject</Label>
            <Input value={editing.subject} onChange={(e) => setEditing((t) => ({ ...t, subject: e.target.value }))} data-testid="template-subject-input" />
          </div>
          <div>
            <Label className="text-sm">Body</Label>
            <Textarea rows={6} value={editing.body_html} onChange={(e) => setEditing((t) => ({ ...t, body_html: e.target.value }))} data-testid="template-body-input" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving} data-testid="template-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="template-save-btn">
              <Save size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save template'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {templates.length === 0 && <p className="text-sm text-muted-foreground">No templates yet.</p>}
          {templates.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3 border rounded-md p-3" data-testid={`template-row-${t.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-sm">{t.name}</div>
                <div className="text-xs text-muted-foreground truncate">{t.subject}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => setEditing(t)} data-testid={`template-edit-${t.id}`}><Pencil size={14} /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(t.id)} className="text-red-600" data-testid={`template-delete-${t.id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
